import { Button } from "../components/ui/button"
import { Brain, Zap, Target, ArrowRight, Users, Flag, Loader2 } from "lucide-react";
import "../constant";

export function MarketingPlan({
    productStage,
    marketingPlan,
    onNext,
    isGenerating,
    onOpenInspiration,
    selectedInspirationCount,
    currentProjectName
}) {
    if (!marketingPlan) {
        return (
            <div className="flex-1 min-w-0 flex items-center justify-center p-4 sm:p-10 text-center bg-gray-50 dark:bg-[#0a0a0a]">
                <div className="space-y-4">
                    <Brain className="w-12 h-12 text-gray-400 mx-auto animate-pulse" />
                    <p className="text-lg font-medium text-gray-600 dark:text-gray-300">
                        Generating your custom marketing plan...
                    </p>
                </div>
            </div>
        )
    }

    const { positioning, marketingStrategy, personas, thisWeeksPlan, recommendedCampaigns, planSizing } = marketingPlan



    return (
        <div className="flex-1 min-w-0 overflow-y-auto py-6 px-4 sm:px-6 lg:px-10 bg-gray-50 dark:bg-[#0a0a0a]">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="space-y-1">
                    {currentProjectName && (
                        <p className="text-xs font-medium uppercase tracking-wider text-orange-500 dark:text-orange-400">
                            {currentProjectName}
                        </p>
                    )}
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                        Here's what we'll do next
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Based on your brand and goals, this is your strategy
                    </p>
                </div>

                {/* Positioning Card */}
                <div className="p-6 rounded-2xl border bg-white border-gray-200 dark:bg-white/5 dark:border-white/10">
                    <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-500/20">
                            <Flag className="w-6 h-6 text-orange-500" />
                        </div>
                        <div className="flex-1 min-w-0 space-y-4">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Brand Positioning
                            </h2>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider mb-2 text-orange-600 dark:text-orange-400">
                                        Core Identity
                                    </p>
                                    <p className="text-sm leading-relaxed font-medium italic text-gray-800 dark:text-gray-200">
                                        "{positioning.coreIdentity}"
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider mb-2 text-red-600 dark:text-red-400">
                                        The Enemy
                                    </p>
                                    <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                                        {positioning.theEnemy}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Marketing Strategy Card */}
                <div className="p-6 rounded-2xl border bg-gradient-to-br from-purple-50 to-white border-purple-200 dark:from-purple-500/10 dark:to-transparent dark:border-purple-500/20">
                    <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-500/20">
                            <Brain className="w-6 h-6 text-purple-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                Marketing Strategy
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider mb-1 text-purple-600 dark:text-purple-400">
                                        What to focus on
                                    </p>
                                    <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                                        {marketingStrategy.whatWeAreDoing}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider mb-1 text-purple-600 dark:text-purple-400">
                                        Why this works
                                    </p>
                                    <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                                        {marketingStrategy.whyItWorks}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium uppercase tracking-wider mb-1 text-purple-600 dark:text-purple-400">
                                        Why now
                                    </p>
                                    <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                                        {marketingStrategy.whyWeAreDoingThisNow || (
                                            productStage === "just-launched"
                                                ? "Your product is still in the early testing stage, so we want fast feedback before scaling ads."
                                                : productStage === "some-sales"
                                                    ? "You have some traction, so now is the time to scale what's working while testing new angles."
                                                    : "You're ready to scale, so we're focusing on maximizing ROAS with proven messaging."
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* This Week's Plan Card */}
                <div className="p-6 rounded-2xl border bg-gradient-to-br from-orange-50 to-white border-orange-200 dark:from-orange-500/10 dark:to-transparent dark:border-orange-500/20">
                    <div className="flex items-start gap-4">
                        <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-500/20">
                            <Zap className="w-6 h-6 text-orange-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                What to create this week
                            </h2>
                            <div className="grid sm:grid-cols-3 gap-4">
                                <div className="p-4 rounded-xl text-center bg-white/80 dark:bg-white/5">
                                    <p className="text-2xl font-bold text-orange-500">{thisWeeksPlan.ugcAds || 0}</p>
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-400">UGC ads</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">(different angles)</p>
                                </div>
                                <div className="p-4 rounded-xl text-center bg-white/80 dark:bg-white/5">
                                    <p className="text-2xl font-bold text-orange-500">{thisWeeksPlan.staticImageAds || 0}</p>
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-400">Static image ad</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">(clear value)</p>
                                </div>
                                <div className="p-4 rounded-xl text-center bg-white/80 dark:bg-white/5">
                                    <p className="text-2xl font-bold text-orange-500">{thisWeeksPlan.organicVideos || 0}</p>
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-400">Organic videos</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">(grow your audience)</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer Personas Section */}
                <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Customer Personas
                    </h2>

                    {planSizing?.personasHeldInReserve > 0 && (
                        <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/20 text-blue-800 dark:text-blue-300 text-sm">
                            We found {planSizing.personasFound} audiences worth targeting. Based on your budget, we recommend starting with {planSizing.campaignsRecommended}.
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                        {personas.map((persona, index) => {
                            const isActive = persona.isActive !== false;
                            return (
                            <div
                                key={index}
                                className={`p-5 rounded-xl border transition-all ${isActive ? "bg-white border-gray-200 dark:bg-white/5 dark:border-white/10" : "bg-gray-50 border-gray-200 opacity-60 dark:bg-black/20 dark:border-white/5"}`}
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 rounded-lg bg-gray-100 dark:bg-white/10">
                                        <Users className={`w-5 h-5 ${isActive ? "text-orange-500" : "text-gray-400"}`} />
                                    </div>
                                    <div className="flex items-center justify-between w-full">
                                        <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                                            {persona.behavioralName}
                                        </h4>
                                        {!isActive && (
                                            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500 bg-gray-200 dark:bg-white/10 px-2 py-0.5 rounded">
                                                Reserved
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-3 text-sm">
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wider mb-1 text-gray-500">Core Desire</p>
                                        <p className="text-gray-800 dark:text-gray-300">{persona.coreDesire}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wider mb-1 text-gray-500">False Belief</p>
                                        <p className="text-gray-800 dark:text-gray-300">{persona.falseBelief}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium uppercase tracking-wider mb-1 text-red-600/80 dark:text-red-400/80">BIGGEST FRUSTRATION</p>
                                        <p className="text-gray-800 dark:text-gray-300">{persona.visceralPain}</p>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                </div>

                {/* Campaign Cards */}
                <div className="space-y-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Recommended Campaigns
                    </h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {recommendedCampaigns.map((campaign, index) => (
                            <div
                                key={index}
                                className="p-5 rounded-xl border transition-all bg-white border-gray-200 hover:border-gray-300 dark:bg-white/5 dark:border-white/10 dark:hover:border-white/20"
                            >
                                <div className="flex items-start gap-3 mb-4">
                                    <div className="p-2 rounded-lg bg-gray-100 dark:bg-white/10">
                                        <Target className="w-5 h-5 text-orange-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="text-base font-semibold truncate text-gray-900 dark:text-white">
                                            {campaign.campaignName}
                                        </h4>
                                        <p className="text-xs font-medium uppercase tracking-wider text-orange-600 dark:text-orange-400">
                                            Angle: {campaign.angle?.angleName}
                                        </p>
                                    </div>
                                </div>
                                <div className="space-y-2 text-sm mb-4">
                                    <div className="flex justify-between items-start gap-4">
                                        <span className="text-xs font-medium uppercase tracking-wider text-gray-500 shrink-0 mt-0.5">Audience</span>
                                        <span className="text-sm text-gray-800 dark:text-gray-300 text-right">{campaign.audience}</span>
                                    </div>
                                    <div className="flex justify-between items-start gap-4">
                                        <span className="text-xs font-medium uppercase tracking-wider text-gray-500 shrink-0 mt-0.5">Platform</span>
                                        <span className="text-sm text-gray-800 dark:text-gray-300 text-right">{campaign.platforms}</span>
                                    </div>
                                    <div className="flex justify-between items-center gap-4">
                                        <span className="text-xs font-medium uppercase tracking-wider text-gray-500 shrink-0">Ads to create</span>
                                        <div className="text-right">
                                            <span className="text-orange-500 font-bold">{campaign.adsToCreate}</span>
                                            {(campaign.ugcAds > 0 || campaign.staticImageAds > 0) && (
                                                <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 ml-1.5">
                                                    · {campaign.ugcAds || 0} video / {campaign.staticImageAds || 0} image
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center gap-4">
                                        <span className="text-xs font-medium uppercase tracking-wider text-gray-500 shrink-0">Organic to create</span>
                                        <span className="text-teal-500 font-bold text-right">{campaign.organicToCreate}</span>
                                    </div>
                                </div>
                                <div className="p-3 rounded-lg space-y-2 bg-gray-50 dark:bg-white/5">
                                    <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                                        Why this campaign
                                    </p>
                                    <div className="space-y-2">
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">What it does</p>
                                            <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">{campaign.whyThisCampaign?.whatItDoes}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Why it works</p>
                                            <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">{campaign.whyThisCampaign?.whyItWorks}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Why now</p>
                                            <p className="text-xs leading-relaxed text-gray-700 dark:text-gray-300">{campaign.whyThisCampaign?.whyNow}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>


                <div className="flex justify-end pt-4 pb-10">
                    <Button
                        onClick={onNext}
                        disabled={isGenerating}
                        className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium"
                    >
                        {isGenerating ? (
                            <>
                                <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                                Generating concepts...
                            </>
                        ) : (
                            <>
                                Review my concepts
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    )
}
